/**
 * Wattec compare page.
 *
 * Up to three PCs side by side, each at the exact version (variant) the
 * customer picks. Data comes from the JSON the section writes into the page
 * (sections/wattec-compare.liquid) — see that file for why specs are
 * resolved the way they are here.
 *
 * Picks are stored where the theme has always kept them ('dt-compare-list',
 * a comma-separated list of product handles) so the compare icons on product
 * cards and the header counter stay in step; the chosen version of each PC is
 * kept alongside in 'wattec-compare-variants'. A comparison can also be opened
 * from a link: ?compare=handle:variantId,handle:variantId
 */
(function () {
  'use strict';

  var LIST_KEY = 'dt-compare-list';
  var VARIANT_KEY = 'wattec-compare-variants';
  var URL_PARAM = 'compare';

  var SPEC_ROWS = [
    { key: 'cpu', label: 'Processor' },
    { key: 'gpu', label: 'Graphics card' },
    { key: 'ram', label: 'Memory (RAM)' },
    { key: 'storage', label: 'Storage' },
    { key: 'motherboard', label: 'Motherboard' },
    { key: 'psu', label: 'Power supply' },
    { key: 'cooler', label: 'CPU cooler' },
    { key: 'case', label: 'Case' }
  ];
  var LATE_ROWS = [
    { key: 'os', label: 'Operating system' },
    { key: 'warranty', label: 'Warranty' },
    { key: 'extras', label: 'Extras' }
  ];
  var CONFIGURABLE = ['cpu', 'gpu', 'ram', 'storage', 'motherboard', 'psu', 'cooler', 'case'];

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /* ---- small helpers ------------------------------------------------------ */

  function esc(text) {
    return String(text == null ? '' : text)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function read(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  function write(key, value) {
    try {
      if (value === null || value === '') window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (e) { /* comparison still works for this visit */ }
  }

  function toast(text, saved) {
    if (window.WattecWishlist && window.WattecWishlist.toast) window.WattecWishlist.toast({ text: text, saved: saved });
  }

  /** Which spec an option describes, from its name ("Graphics Card ", "Graphic card", "CPU"…). */
  function optionKey(name) {
    var n = String(name || '').toLowerCase().trim();
    if (/\b(cpu|processor)\b/.test(n)) return 'cpu';
    if (/graphic|gpu|video card/.test(n)) return 'gpu';
    if (/\b(ram|memory)\b/.test(n)) return 'ram';
    if (/storage|ssd|nvme|hard drive/.test(n)) return 'storage';
    if (/colou?r/.test(n)) return 'colour';
    if (n === 'title') return 'title';
    return 'other';
  }

  /**
   * Tokens used to decide whether the product's own spec text describes the
   * same part as a variant's option value. "RTX 5070 Ti" and "RTX 5070Ti" are
   * the same card, but "RTX 5070" is NOT an RTX 5070 Ti — so suffixes like
   * Ti / Super / XT are fused onto the model number before comparing.
   */
  var BRAND_WORDS = { amd: 1, nvidia: 1, geforce: 1, intel: 1, radeon: 1, core: 1 };
  function tokens(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[–—|(),/]/g, ' ')
      .replace(/(\d)\s+(ti|super|xt|xtx|x3d)\b/g, '$1$2')
      .split(/[^a-z0-9]+/)
      .filter(function (t) { return t && !BRAND_WORDS[t]; });
  }

  function describesSamePart(specText, optionValue) {
    var s = tokens(specText);
    var v = tokens(optionValue);
    if (!v.length || !s.length) return false;
    for (var i = 0; i + v.length <= s.length; i++) {
      var ok = true;
      for (var j = 0; j < v.length; j++) {
        if (s[i + j] !== v[j]) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  }

  ready(function () {
    var root = document.querySelector('[data-wcmp]');
    if (!root) return;

    var catalog = [];
    try { catalog = JSON.parse(root.querySelector('[data-wcmp-catalog]').textContent); } catch (e) { catalog = []; }
    var byHandle = {};
    catalog.forEach(function (p) { byHandle[p.handle] = p; });

    var MAX = parseInt(root.getAttribute('data-max'), 10) || 3;
    var tableWrap = root.querySelector('[data-wcmp-table]');
    var bar = root.querySelector('[data-wcmp-bar]');
    var diffToggle = root.querySelector('[data-wcmp-diff]');
    var routeRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    /* ---- state ------------------------------------------------------------ */

    var slots = []; // [{ handle, variantId }]
    // True while showing the suggested starter PCs: they aren't the
    // customer's picks, so they aren't saved or counted until changed.
    var usingDefaults = false;

    function variantOf(slot) {
      var product = byHandle[slot.handle];
      if (!product) return null;
      var found = null;
      product.variants.forEach(function (v) { if (v.id === slot.variantId) found = v; });
      if (!found) {
        product.variants.forEach(function (v) { if (!found && v.available) found = v; });
        found = found || product.variants[0];
        slot.variantId = found ? found.id : null;
      }
      return found;
    }

    function loadState() {
      var fromUrl = new URLSearchParams(window.location.search).get(URL_PARAM);
      var picks = [];
      usingDefaults = false;
      if (fromUrl) {
        fromUrl.split(',').forEach(function (part) {
          var bits = part.split(':');
          if (byHandle[bits[0]]) picks.push({ handle: bits[0], variantId: parseInt(bits[1], 10) || null });
        });
      }
      if (!picks.length) {
        var variants = {};
        try { variants = JSON.parse(read(VARIANT_KEY)) || {}; } catch (e) { variants = {}; }
        (read(LIST_KEY) || '').split(',').forEach(function (handle) {
          handle = handle.trim();
          if (byHandle[handle]) picks.push({ handle: handle, variantId: variants[handle] || null });
        });
      }
      if (!picks.length) {
        var defaults = (root.getAttribute('data-default') || '').split(',').filter(function (h) { return byHandle[h]; });
        if (!defaults.length) {
          defaults = catalog.filter(function (p) { return p.range === 'signature-series' && p.available; })
            .slice(0, MAX).map(function (p) { return p.handle; });
        }
        picks = defaults.map(function (h) { return { handle: h, variantId: null }; });
        usingDefaults = picks.length > 0;
      }
      var seen = {};
      slots = picks.filter(function (p) {
        if (seen[p.handle]) return false;
        seen[p.handle] = true;
        return true;
      }).slice(0, MAX);
      slots.forEach(variantOf);
    }

    function saveState() {
      if (usingDefaults) {
        paintGlanceChips();
        return;
      }
      write(LIST_KEY, slots.map(function (s) { return s.handle; }).join(','));
      var variants = {};
      slots.forEach(function (s) { if (s.variantId) variants[s.handle] = s.variantId; });
      write(VARIANT_KEY, slots.length ? JSON.stringify(variants) : null);

      var url = new URL(window.location.href);
      if (slots.length) url.searchParams.set(URL_PARAM, shareValue());
      else url.searchParams.delete(URL_PARAM);
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);

      updateHeaderCount();
      paintGlanceChips();
    }

    function commit() {
      usingDefaults = false;
      saveState();
    }

    function shareValue() {
      return slots.map(function (s) { return s.handle + ':' + s.variantId; }).join(',');
    }

    function updateHeaderCount() {
      var count = slots.length;
      document.querySelectorAll('.dtxc-compare-count').forEach(function (badge) {
        var span = badge.querySelector('.grid-count-bubble span');
        if (span) span.textContent = count;
        badge.setAttribute('count', count);
        badge.classList.toggle('count-zero', count === 0);
        badge.classList.toggle('count-exist', count > 0);
      });
    }

    /* ---- working out a slot's specs --------------------------------------- */

    function resolve(slot) {
      var product = byHandle[slot.handle];
      var variant = variantOf(slot);
      var specs = {};
      Object.keys(product.specs).forEach(function (k) { specs[k] = product.specs[k] || ''; });

      var others = {};
      var colour = '';
      product.options.forEach(function (name, index) {
        var key = optionKey(name);
        var value = variant && variant.options ? variant.options[index] : '';
        if (!value) return;
        if (key === 'cpu' || key === 'gpu' || key === 'ram' || key === 'storage') {
          if (!describesSamePart(specs[key], value)) specs[key] = value;
        } else if (key === 'colour') {
          colour = value;
        } else if (key === 'other') {
          others[String(name).trim()] = value;
        }
      });

      if (product.custom) {
        CONFIGURABLE.forEach(function (k) { if (!specs[k]) specs[k] = '__choose__'; });
      }

      return { product: product, variant: variant, specs: specs, colour: colour, others: others };
    }

    /* ---- drawing ---------------------------------------------------------- */

    function stars(rating) {
      var full = Math.round(rating * 2) / 2;
      var html = '';
      for (var i = 1; i <= 5; i++) {
        var cls = full >= i ? 'is-on' : (full >= i - 0.5 ? 'is-half' : '');
        html += '<span class="wcmp-star ' + cls + '"></span>';
      }
      return html;
    }

    function headCell(slot, index, r) {
      if (!slot) {
        return '<th scope="col" class="wcmp-col wcmp-col--empty">' +
          '<div class="wcmp-add">' +
            '<div class="wcmp-add__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg></div>' +
            '<p class="wcmp-add__label">Add a PC to compare</p>' +
            '<button type="button" class="wcmp-pick-btn" data-wcmp-open-picker="' + index + '" aria-haspopup="dialog">' +
              '<span>Choose a PC</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg></button>' +
          '</div></th>';
      }

      var p = r.product;
      var v = r.variant;
      var image = (v && v.image) || p.image;
      var optionsHtml = '';
      if (!(p.options.length === 1 && optionKey(p.options[0]) === 'title')) {
        optionsHtml = '<div class="wcmp-variant">' + p.options.map(function (name, oi) {
          var values = [];
          p.variants.forEach(function (pv) {
            if (values.indexOf(pv.options[oi]) === -1) values.push(pv.options[oi]);
          });
          var id = 'WcmpOpt-' + index + '-' + oi;
          return '<div class="wcmp-variant__row"><label for="' + id + '">' + esc(String(name).trim()) + '</label>' +
            '<div class="wcmp-select wcmp-select--sm"><select id="' + id + '" data-wcmp-option="' + index + '" data-option-index="' + oi + '">' +
            values.map(function (val) {
              return '<option' + (v && v.options[oi] === val ? ' selected' : '') + '>' + esc(val) + '</option>';
            }).join('') +
            '</select><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg></div></div>';
        }).join('') + '</div>';
      }

      var productUrl = p.url + (v && p.variants.length > 1 ? '?variant=' + v.id : '');
      var cta;
      if (p.custom) {
        cta = '<a class="wcmp-btn wcmp-btn--primary" href="' + esc(p.url) + '">Configure this PC</a>';
      } else if (v && v.available) {
        cta = '<button type="button" class="wcmp-btn wcmp-btn--primary" data-wcmp-cart="' + v.id + '"><span>Add to basket</span></button>';
      } else {
        cta = '<button type="button" class="wcmp-btn wcmp-btn--primary" disabled>Sold out</button>';
      }

      return '<th scope="col" class="wcmp-col" data-slot="' + index + '">' +
        '<button type="button" class="wcmp-remove" data-wcmp-remove="' + index + '" aria-label="Remove ' + esc(p.title) + ' from the comparison">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
        '<a class="wcmp-col__media" href="' + esc(productUrl) + '" tabindex="-1" aria-hidden="true">' +
          (image ? '<img src="' + esc(image) + '" alt="" loading="lazy" width="300" height="300">' : '') +
        '</a>' +
        (p.rangeLabel ? '<p class="wcmp-col__range">' + esc(p.rangeLabel) + '</p>' : '') +
        '<h3 class="wcmp-col__title"><a href="' + esc(productUrl) + '">' + esc(p.title) + '</a></h3>' +
        (p.rating ? '<p class="wcmp-col__rating" aria-label="Rated ' + p.rating + ' out of 5 from ' + p.ratingCount + ' reviews">' + stars(p.rating) +
          '<span>' + Number(p.rating).toFixed(1) + ' (' + p.ratingCount + ')</span></p>' : '') +
        optionsHtml +
        '<p class="wcmp-col__price">' + (p.custom ? '<span class="wcmp-from">From</span> ' : '') +
          '<span class="wcmp-col__amount">' + esc(v ? v.priceText : '') + '</span>' +
          (v && v.compareAtText ? ' <s>' + esc(v.compareAtText) + '</s>' : '') + '</p>' +
        cta +
        '<a class="wcmp-col__link" href="' + esc(productUrl) + '">' + (p.custom ? 'See what you can choose' : 'View full details') + '</a>' +
        '<div class="wcmp-col__change">' +
          '<button type="button" class="wcmp-swap-btn" data-wcmp-open-picker="' + index + '" aria-haspopup="dialog" aria-label="Swap ' + esc(p.title) + ' for another PC">' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7h11l-3-3M17 17H6l3 3"/></svg><span class="wcmp-swap-long">Swap for another PC</span><span class="wcmp-swap-short">Swap PC</span></button></div>' +
      '</th>';
    }

    function valueHtml(value) {
      if (value === '__choose__') return '<span class="wcmp-choose">Your choice — pick it when you configure</span>';
      if (!value) return '<span class="wcmp-empty" aria-label="Not listed">—</span>';
      return esc(value);
    }

    function render() {
      if (!catalog.length) {
        tableWrap.innerHTML = '<p class="wcmp-note">No PCs are available to compare right now.</p>';
        return;
      }
      var resolved = slots.map(resolve);
      var columns = [];
      for (var i = 0; i < MAX; i++) columns.push(i < slots.length ? slots[i] : null);
      // On a phone, only one empty "add" column is shown, so filled PCs get the room.
      var emptyShown = false;

      var head = '<thead><tr><td class="wcmp-label-cell"><span class="wcmp-count">' +
        (slots.length ? slots.length + ' of ' + MAX + ' PCs' : 'Pick up to ' + MAX + ' PCs') + '</span></td>' +
        columns.map(function (slot, idx) {
          var cell = headCell(slot, idx, slot ? resolved[idx] : null);
          if (!slot) {
            cell = cell.replace('class="wcmp-col wcmp-col--empty"', 'class="wcmp-col wcmp-col--empty' + (emptyShown ? ' is-extra' : '') + '"');
            emptyShown = true;
          }
          return cell;
        }).join('') + '</tr></thead>';

      var rows = [];
      function addRow(key, label, values, extraClass) {
        var filled = values.filter(function (v) { return v && v !== '__choose__'; });
        if (!filled.length && values.indexOf('__choose__') === -1) return;
        var same = values.length > 1 && values.every(function (v) { return v === values[0]; });
        rows.push('<tr class="wcmp-row' + (same ? ' is-same' : '') + (extraClass ? ' ' + extraClass : '') + '" data-row="' + esc(key) + '">' +
          '<th scope="row" class="wcmp-label-cell">' + esc(label) + '</th>' +
          columns.map(function (slot, idx) {
            if (!slot) return '<td class="wcmp-cell wcmp-cell--empty' + (idx > slots.length ? ' is-extra' : '') + '"></td>';
            return '<td class="wcmp-cell" data-label="' + esc(label) + '">' + valueHtml(values[idx]) + '</td>';
          }).join('') + '</tr>');
      }

      if (slots.length) {
        // Price, with the cheapest marked when there's a choice.
        var prices = resolved.map(function (r) { return r.variant ? r.variant.price : null; });
        var lowest = Math.min.apply(null, prices.filter(function (x) { return x !== null; }));
        rows.push('<tr class="wcmp-row wcmp-row--price" data-row="price"><th scope="row" class="wcmp-label-cell">Price</th>' +
          columns.map(function (slot, idx) {
            if (!slot) return '<td class="wcmp-cell wcmp-cell--empty' + (idx > slots.length ? ' is-extra' : '') + '"></td>';
            var r = resolved[idx];
            var tag = slots.length > 1 && r.variant && r.variant.price === lowest && prices.filter(function (x) { return x === lowest; }).length === 1
              ? '<span class="wcmp-tag">Lowest price</span>' : '';
            return '<td class="wcmp-cell" data-label="Price"><strong class="wcmp-price">' + (r.product.custom ? '<span class="wcmp-from">From</span> ' : '') +
              esc(r.variant ? r.variant.priceText : '') + '</strong>' +
              (r.variant && r.variant.compareAtText ? ' <s class="wcmp-was">' + esc(r.variant.compareAtText) + '</s>' : '') + tag + '</td>';
          }).join('') + '</tr>');

        SPEC_ROWS.forEach(function (row) {
          addRow(row.key, row.label, resolved.map(function (r) { return r.specs[row.key]; }));
        });

        addRow('colour', 'Colour', resolved.map(function (r) { return r.colour; }));

        var otherNames = [];
        resolved.forEach(function (r) {
          Object.keys(r.others).forEach(function (n) { if (otherNames.indexOf(n) === -1) otherNames.push(n); });
        });
        otherNames.forEach(function (n) {
          addRow('opt-' + n, n, resolved.map(function (r) { return r.others[n] || ''; }));
        });

        LATE_ROWS.forEach(function (row) {
          addRow(row.key, row.label, resolved.map(function (r) { return r.specs[row.key]; }));
        });

        rows.push('<tr class="wcmp-row" data-row="stock"><th scope="row" class="wcmp-label-cell">Availability</th>' +
          columns.map(function (slot, idx) {
            if (!slot) return '<td class="wcmp-cell wcmp-cell--empty' + (idx > slots.length ? ' is-extra' : '') + '"></td>';
            var r = resolved[idx];
            var html = r.product.custom
              ? '<span class="wcmp-stock wcmp-stock--made">Built to order</span>'
              : (r.variant && r.variant.available
                ? '<span class="wcmp-stock wcmp-stock--in">In stock</span>'
                : '<span class="wcmp-stock wcmp-stock--out">Sold out in this version</span>');
            return '<td class="wcmp-cell" data-label="Availability">' + html + '</td>';
          }).join('') + '</tr>');
      }

      tableWrap.innerHTML =
        '<div class="wcmp-sticky" data-wcmp-sticky aria-hidden="true"></div>' +
        '<table class="wcmp-table" data-cols="' + slots.length + '" style="--wcmp-cols:' + Math.min(MAX, slots.length + (slots.length < MAX ? 1 : 0)) + ';--wcmp-filled:' + Math.max(slots.length, 1) + '">' +
          '<caption class="visually-hidden">Comparison of the chosen PCs</caption>' +
          head + '<tbody>' + rows.join('') + '</tbody></table>' +
        (slots.length ? '' : '<p class="wcmp-note wcmp-note--center">Choose a PC above, or tap Compare on any PC in the table below.</p>') +
        (usingDefaults && slots.length ? '<p class="wcmp-note wcmp-note--center">Popular picks to get you started — swap any of them for the PCs you’re considering.</p>' : '');

      bar.hidden = slots.length < 1;
      root.classList.toggle('is-diff', !!(diffToggle && diffToggle.checked));
      buildSticky(resolved);
    }

    /* ---- compact sticky header while scrolling the table ------------------ */

    function buildSticky(resolved) {
      var sticky = tableWrap.querySelector('[data-wcmp-sticky]');
      var headRow = tableWrap.querySelector('thead');
      if (!sticky || !headRow || !slots.length) return;
      sticky.innerHTML = '<div class="wcmp-sticky__inner"><span class="wcmp-sticky__label">Comparing</span>' + resolved.map(function (r) {
        var img = (r.variant && r.variant.image) || r.product.thumb || r.product.image;
        return '<div class="wcmp-sticky__item">' + (img ? '<img src="' + esc(img) + '" alt="" width="40" height="40">' : '') +
          '<div><span class="wcmp-sticky__title">' + esc(r.product.title) + '</span>' +
          '<span class="wcmp-sticky__price">' + esc(r.variant ? r.variant.priceText : '') + '</span></div></div>';
      }).join('') + '</div>';

      updateSticky();
    }

    // The site header stays pinned while scrolling, so the mini header sits
    // just beneath whatever height it has at the moment.
    function headerBottom() {
      var bottom = 0;
      document.querySelectorAll('header.header, sticky-header, .section-header, .shopify-section-header-sticky').forEach(function (el) {
        var rect = el.getBoundingClientRect();
        // Only a header pinned to the top of the screen counts.
        if (rect.height > 0 && rect.top <= 1 && rect.bottom > bottom) bottom = rect.bottom;
      });
      return Math.round(bottom);
    }

    function updateSticky() {
      var sticky = tableWrap.querySelector('[data-wcmp-sticky]');
      var table = tableWrap.querySelector('table');
      var headRow = tableWrap.querySelector('thead');
      if (!sticky || !table || !headRow || !slots.length) return;
      var top = headerBottom();
      var show = headRow.getBoundingClientRect().bottom < top && table.getBoundingClientRect().bottom > top + 140;
      var rect = table.getBoundingClientRect();
      sticky.style.top = top + 'px';
      sticky.style.left = Math.round(rect.left) + 'px';
      sticky.style.width = Math.round(rect.width) + 'px';
      sticky.classList.toggle('is-visible', show);
    }

    var stickyTicking = false;
    window.addEventListener('scroll', function () {
      if (stickyTicking) return;
      stickyTicking = true;
      requestAnimationFrame(function () { stickyTicking = false; updateSticky(); });
    }, { passive: true });
    window.addEventListener('resize', updateSticky);

    /* ---- the at-a-glance table's Compare buttons -------------------------- */

    function paintGlanceChips() {
      root.querySelectorAll('[data-wcmp-add-handle]').forEach(function (chip) {
        var on = slots.some(function (s) { return s.handle === chip.getAttribute('data-wcmp-add-handle'); });
        chip.classList.toggle('is-added', on);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function addHandle(handle, index) {
      var product = byHandle[handle];
      if (!product) return false;
      var existing = -1;
      slots.forEach(function (s, i) { if (s.handle === handle) existing = i; });
      if (existing !== -1) return true;
      var slot = { handle: handle, variantId: null };
      variantOf(slot);
      if (typeof index === 'number' && index < slots.length) slots[index] = slot;
      else if (slots.length < MAX) slots.push(slot);
      else {
        toast('You can compare up to ' + MAX + ' PCs at once — remove one first.');
        return false;
      }
      return true;
    }

    /* ---- PC picker: photo, name, range and price for every PC -------------
       Used both to swap a PC and to fill an empty column. A standard dropdown
       can't show pictures, so this is a dialog: a centred window on larger
       screens, a sheet rising from the bottom on a phone. */

    var picker = (function () {
      var RANGES = [
        { range: 'all', label: 'All' },
        { range: 'signature-series', label: 'Signature Series' },
        { range: 'eco-rebuilds', label: 'Eco Rebuilds' },
        { range: 'custom-builds', label: 'Custom builds' }
      ];
      var el = null;
      var listEl, searchEl, titleEl;
      var slotIndex = 0;
      var returnFocus = null;
      var activeRange = 'all';
      var closeTimer = null;

      function cheapest(p) {
        var best = null;
        p.variants.forEach(function (v) { if (!best || v.price < best.price) best = v; });
        return best;
      }

      function build() {
        el = document.createElement('div');
        el.className = 'wcmp-picker';
        el.hidden = true;
        el.innerHTML =
          '<div class="wcmp-picker__backdrop" data-picker-close></div>' +
          '<div class="wcmp-picker__panel" role="dialog" aria-modal="true" aria-labelledby="WcmpPickerTitle">' +
            '<div class="wcmp-picker__head">' +
              '<h2 id="WcmpPickerTitle" class="wcmp-picker__title"></h2>' +
              '<button type="button" class="wcmp-picker__close" data-picker-close aria-label="Close">' +
                '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
            '</div>' +
            '<div class="wcmp-picker__tools">' +
              '<label class="wcmp-picker__search"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M20 20l-4-4"/></svg>' +
                '<span class="visually-hidden">Search PCs</span>' +
                '<input type="search" placeholder="Search by name, CPU or graphics card" autocomplete="off" data-picker-search></label>' +
              '<div class="wcmp-picker__ranges" role="group" aria-label="Filter by range">' +
                RANGES.filter(function (r) {
                  return r.range === 'all' || catalog.some(function (p) { return p.range === r.range; });
                }).map(function (r) {
                  return '<button type="button" class="wcmp-chip' + (r.range === 'all' ? ' is-active' : '') + '" data-picker-range="' + r.range + '" aria-pressed="' + (r.range === 'all') + '">' + esc(r.label) + '</button>';
                }).join('') +
              '</div>' +
            '</div>' +
            '<ul class="wcmp-picker__list" data-picker-list></ul>' +
          '</div>';
        // At the top level of the page, so nothing on the page can sit above it.
        document.body.appendChild(el);
        listEl = el.querySelector('[data-picker-list]');
        searchEl = el.querySelector('[data-picker-search]');
        titleEl = el.querySelector('.wcmp-picker__title');

        el.addEventListener('click', function (event) {
          event.stopPropagation();
          if (event.target.closest('[data-picker-close]')) { close(); return; }
          var range = event.target.closest('[data-picker-range]');
          if (range) {
            activeRange = range.getAttribute('data-picker-range');
            el.querySelectorAll('[data-picker-range]').forEach(function (b) {
              b.classList.toggle('is-active', b === range);
              b.setAttribute('aria-pressed', b === range ? 'true' : 'false');
            });
            fill();
            return;
          }
          var item = event.target.closest('[data-picker-handle]');
          if (item && !item.disabled) choose(item.getAttribute('data-picker-handle'));
        });
        searchEl.addEventListener('input', fill);
        el.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') { event.preventDefault(); close(); return; }
          if (event.key !== 'Tab') return;
          // Keep keyboard focus inside the dialog while it's open.
          var focusable = Array.prototype.filter.call(
            el.querySelectorAll('button, input'),
            function (n) { return !n.disabled && n.offsetParent !== null; }
          );
          if (!focusable.length) return;
          var first = focusable[0];
          var last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        });
      }

      function fill() {
        var q = searchEl.value.trim().toLowerCase();
        var current = slots[slotIndex] ? slots[slotIndex].handle : null;
        var used = {};
        slots.forEach(function (s) { used[s.handle] = true; });
        var items = catalog.filter(function (p) {
          if (activeRange !== 'all' && p.range !== activeRange) return false;
          if (!q) return true;
          var hay = (p.title + ' ' + (p.specs.cpu || '') + ' ' + (p.specs.gpu || '') + ' ' +
            p.variants.map(function (v) { return v.title; }).join(' ')).toLowerCase();
          return q.split(/\s+/).every(function (word) { return hay.indexOf(word) !== -1; });
        });
        if (!items.length) {
          listEl.innerHTML = '<li class="wcmp-picker__none">No PCs match that search.</li>';
          return;
        }
        listEl.innerHTML = items.map(function (p) {
          var low = cheapest(p);
          var varies = p.variants.some(function (v) { return v.price !== low.price; });
          var isCurrent = p.handle === current;
          var inUse = used[p.handle] && !isCurrent;
          var note = isCurrent ? 'In this column now' : (inUse ? 'Already being compared' : (p.available ? '' : 'Sold out'));
          var specLine = p.custom ? 'Choose every part yourself' : [p.specs.cpu, p.specs.gpu].filter(Boolean).join(' · ');
          return '<li><button type="button" class="wcmp-picker__item' + (isCurrent ? ' is-current' : '') + '" data-picker-handle="' + esc(p.handle) + '"' + (isCurrent || inUse ? ' disabled' : '') + '>' +
            '<span class="wcmp-picker__img">' + (p.thumb ? '<img src="' + esc(p.thumb) + '" alt="" decoding="async" width="72" height="72">' : '') + '</span>' +
            '<span class="wcmp-picker__info">' +
              (p.rangeLabel ? '<span class="wcmp-picker__range">' + esc(p.rangeLabel) + '</span>' : '') +
              '<span class="wcmp-picker__name">' + esc(p.title) + '</span>' +
              (specLine ? '<span class="wcmp-picker__spec">' + esc(specLine) + '</span>' : '') +
              (note ? '<span class="wcmp-picker__note">' + esc(note) + '</span>' : '') +
            '</span>' +
            '<span class="wcmp-picker__price">' + ((varies || p.custom) ? '<span class="wcmp-from">From</span>' : '') + esc(low ? low.priceText : '') + '</span>' +
          '</button></li>';
        }).join('');
        listEl.scrollTop = 0;
      }

      function open(index, trigger) {
        if (!el) build();
        clearTimeout(closeTimer);
        slotIndex = index;
        returnFocus = trigger || null;
        var current = slots[index] ? byHandle[slots[index].handle] : null;
        titleEl.textContent = current ? 'Swap ' + current.title + ' for…' : 'Choose a PC to compare';
        searchEl.value = '';
        fill();
        el.hidden = false;
        document.documentElement.classList.add('wcmp-picker-open');
        requestAnimationFrame(function () {
          el.classList.add('is-open');
          // On a touch screen, focusing the search box would push the keyboard
          // up over the list, so the first PC takes focus instead.
          var coarse = window.matchMedia('(pointer: coarse)').matches;
          var target = coarse ? listEl.querySelector('.wcmp-picker__item:not([disabled])') : searchEl;
          if (target) target.focus({ preventScroll: true });
        });
      }

      function close() {
        if (!el || el.hidden) return;
        el.classList.remove('is-open');
        document.documentElement.classList.remove('wcmp-picker-open');
        closeTimer = setTimeout(function () { el.hidden = true; }, 220);
        if (returnFocus && document.body.contains(returnFocus)) returnFocus.focus({ preventScroll: true });
      }

      function choose(handle) {
        var index = slotIndex;
        close();
        if (addHandle(handle, index)) {
          commit();
          render();
          var col = tableWrap.querySelector('.wcmp-col[data-slot="' + Math.min(index, slots.length - 1) + '"] .wcmp-col__title a');
          if (col) col.focus({ preventScroll: true });
          toast(byHandle[handle].title + ' added to the comparison', true);
        }
      }

      return { open: open, close: close };
    })();


    /* ---- events ------------------------------------------------------------ */

    root.addEventListener('change', function (event) {
      var opt = event.target.closest('[data-wcmp-option]');
      if (opt) {
        var slotIndex = parseInt(opt.getAttribute('data-wcmp-option'), 10);
        var slot = slots[slotIndex];
        var product = byHandle[slot.handle];
        var chosen = [];
        tableWrap.querySelectorAll('[data-wcmp-option="' + slotIndex + '"]').forEach(function (sel) {
          chosen[parseInt(sel.getAttribute('data-option-index'), 10)] = sel.value;
        });
        var changedIndex = parseInt(opt.getAttribute('data-option-index'), 10);
        var match = null;
        product.variants.forEach(function (v) {
          if (!match && v.options.every(function (val, k) { return val === chosen[k]; })) match = v;
        });
        // That exact combination doesn't exist: keep the option just changed
        // and take the closest version that has it.
        if (!match) {
          product.variants.forEach(function (v) {
            if (!match && v.options[changedIndex] === chosen[changedIndex] && v.available) match = v;
          });
          product.variants.forEach(function (v) {
            if (!match && v.options[changedIndex] === chosen[changedIndex]) match = v;
          });
        }
        if (match) slot.variantId = match.id;
        commit();
        render();
        var again = tableWrap.querySelector('#WcmpOpt-' + slotIndex + '-' + changedIndex);
        if (again) again.focus({ preventScroll: true });
        return;
      }

      if (event.target === diffToggle) {
        root.classList.toggle('is-diff', diffToggle.checked);
      }
    });

    root.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-wcmp-open-picker]');
      if (opener) {
        picker.open(parseInt(opener.getAttribute('data-wcmp-open-picker'), 10), opener);
        return;
      }

      var remove = event.target.closest('[data-wcmp-remove]');
      if (remove) {
        var idx = parseInt(remove.getAttribute('data-wcmp-remove'), 10);
        var removed = slots.splice(idx, 1)[0];
        commit();
        render();
        if (removed && byHandle[removed.handle]) toast(byHandle[removed.handle].title + ' removed from the comparison');
        return;
      }

      var cart = event.target.closest('[data-wcmp-cart]');
      if (cart) {
        addToBasket(cart);
        return;
      }

      var chip = event.target.closest('[data-wcmp-add-handle]');
      if (chip) {
        var handle = chip.getAttribute('data-wcmp-add-handle');
        var at = -1;
        slots.forEach(function (s, i) { if (s.handle === handle) at = i; });
        if (at !== -1) {
          slots.splice(at, 1);
          commit();
          render();
          return;
        }
        if (addHandle(handle)) {
          commit();
          render();
          toast(byHandle[handle].title + ' added to the comparison', true);
          var tool = root.querySelector('.wcmp-tool');
          if (tool) tool.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        }
        return;
      }

      if (event.target.closest('[data-wcmp-show-all]')) {
        var glance = root.querySelector('[data-wcmp-glance]');
        glance.classList.remove('is-collapsed');
        var more = root.querySelector('[data-wcmp-more]');
        if (more) more.hidden = true;
        return;
      }

      var filter = event.target.closest('[data-wcmp-filter]');
      if (filter) {
        var range = filter.getAttribute('data-wcmp-filter');
        root.querySelectorAll('[data-wcmp-filter]').forEach(function (b) {
          b.classList.toggle('is-active', b === filter);
          b.setAttribute('aria-pressed', b === filter ? 'true' : 'false');
        });
        root.querySelectorAll('[data-wcmp-range]').forEach(function (row) {
          row.hidden = range !== 'all' && row.getAttribute('data-wcmp-range') !== range;
        });
        // A filtered list is short enough to show whole.
        if (range !== 'all') {
          root.querySelector('[data-wcmp-glance]').classList.remove('is-collapsed');
          var moreBtn = root.querySelector('[data-wcmp-more]');
          if (moreBtn) moreBtn.hidden = true;
        }
        return;
      }

      if (event.target.closest('[data-wcmp-share]')) {
        share();
        return;
      }

      var clear = event.target.closest('[data-wcmp-clear]');
      if (clear) {
        var before = slots.slice();
        slots = [];
        commit();
        render();
        if (window.WattecWishlist && window.WattecWishlist.toast) {
          window.WattecWishlist.toast({
            text: 'Comparison cleared',
            actionLabel: 'Undo',
            action: function () { slots = before; commit(); render(); }
          });
        }
      }
    });

    /* ---- add to basket ------------------------------------------------------ */

    function addToBasket(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      var label = button.querySelector('span');
      var original = label.textContent;
      var drawer = document.querySelector('cart-drawer');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-busy');
      label.textContent = 'Adding…';

      var body = new FormData();
      body.append('id', button.getAttribute('data-wcmp-cart'));
      body.append('quantity', '1');
      if (drawer && drawer.getSectionsToRender) {
        body.append('sections', drawer.getSectionsToRender().map(function (s) { return s.id; }));
        body.append('sections_url', window.location.pathname);
      }

      fetch(routeRoot + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then(function (response) { return response.json(); })
        .then(function (result) {
          if (result.status) {
            toast(result.description || result.message || 'Sorry, this PC couldn’t be added to your basket.');
            label.textContent = original;
            return;
          }
          label.textContent = 'Added to basket';
          button.classList.add('is-done');
          setTimeout(function () { label.textContent = original; button.classList.remove('is-done'); }, 2500);
          if (drawer && drawer.renderContents && result.sections) {
            drawer.classList.remove('is-empty');
            try { drawer.renderContents(result); } catch (e) { window.location = (window.routes && window.routes.cart_url) || '/cart'; }
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function () {
          label.textContent = original;
          toast('Sorry, something went wrong adding this PC to your basket. Please try again.');
        })
        .then(function () {
          button.removeAttribute('aria-disabled');
          button.classList.remove('is-busy');
        });
    }

    /* ---- sharing ------------------------------------------------------------ */

    function share() {
      if (!slots.length) return;
      var url = window.location.origin + window.location.pathname + '?' + URL_PARAM + '=' + encodeURIComponent(shareValue()).replace(/%2C/g, ',').replace(/%3A/g, ':');
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        navigator.share({ title: 'Wattec PC comparison', text: 'Compare these PCs at Wattec', url: url }).catch(function () {});
        return;
      }
      var done = function () { toast('Link copied — paste it anywhere to share this comparison.', true); };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(done, function () { window.prompt('Copy this link to share the comparison:', url); });
      } else {
        window.prompt('Copy this link to share the comparison:', url);
      }
    }

    // A pick made in another tab (e.g. a card's compare icon) shows up here too.
    window.addEventListener('storage', function (event) {
      if (event.key !== LIST_KEY && event.key !== VARIANT_KEY) return;
      var url = new URL(window.location.href);
      url.searchParams.delete(URL_PARAM);
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      loadState();
      render();
      paintGlanceChips();
    });

    loadState();
    saveState();
    render();
  });
})();
